/**
 * <cart-drawer> custom element — pure surface
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The drawer never talks to the Cart API. `<cart-engine>` is the single cart
 * writer (issue #20); the drawer translates clicks into engine actions and
 * listens to the frozen DOM event vocabulary (assets/fw-events.js).
 *
 * One-way flow:
 *   click (+/-/remove/plan) → engine action → POST /cart/*.js (+ sections=)
 *   → store:cart:update { cart, sections } → section-morph-adapter morphs the
 *   whole drawer section (header count, lines, subtotal, upsells). Nothing in
 *   the drawer is hand-patched — see docs/components.md.
 *
 * Open policy lives here, not in the store: `store:cart:added` carries the
 * trigger's intent as `detail.openDrawer` (from the `openCart` option) and the
 * drawer opens itself unless intent says no.
 *
 * All item HTML — including bundle component / gift sub-lists
 * (cart-item-components) — is rendered server-side by Liquid, then morphed in by
 * <cart-engine>'s data-cart-section bundled-section-rendering response
 * (assets/section-morph-adapter.js) — the drawer no longer fetches the section.
 */

// Frozen event vocabulary (window.Theme.events is loaded via defer before any
// module executes; literals double as a guard against wiring bugs).
const NAMES = (window.Theme && window.Theme.events && window.Theme.events.NAMES) || {
  cartUpdate: 'store:cart:update',
  cartAdded: 'store:cart:added',
  cartError: 'store:cart:error',
};

class CartDrawer extends HTMLElement {
  connectedCallback() {
    try {
      this.dialogId = this.getAttribute('data-dialog-id') || 'cart';
      this._planCache = Object.create(null);

      this.addEventListener('click', this.#onClick, true);
      this.addEventListener('change', this.#onChange, true);
      this.addEventListener('focusin', this.#onPlanFocus, true);

      // The drawer owns its open-on-add policy. The store announces the add
      // (its refresh already dispatched store:cart:update with the fresh cart,
      // which morphed us) and carries the caller's intent in the detail.
      this._onAdded = (ev) => {
        try {
          const openDrawer = !(ev && ev.detail && ev.detail.openDrawer === false);
          if (openDrawer) this.#open();
        } catch (_) {}
      };
      this._onUpdate = (ev) => {
        try {
          const cart = ev && ev.detail && ev.detail.cart;
          if (cart) this.#applyCart(cart);
        } catch (_) {}
      };
      this._onError = (ev) => {
        try { console.warn('[cart:error]', ev.detail); } catch (_) {}
      };
      document.addEventListener(NAMES.cartAdded, this._onAdded);
      document.addEventListener(NAMES.cartUpdate, this._onUpdate);
      document.addEventListener(NAMES.cartError, this._onError);
    } catch (_) {}
  }

  disconnectedCallback() {
    try {
      this.removeEventListener('click', this.#onClick, true);
      this.removeEventListener('change', this.#onChange, true);
      this.removeEventListener('focusin', this.#onPlanFocus, true);
      if (this._onAdded) document.removeEventListener(NAMES.cartAdded, this._onAdded);
      if (this._onUpdate) document.removeEventListener(NAMES.cartUpdate, this._onUpdate);
      if (this._onError) document.removeEventListener(NAMES.cartError, this._onError);
    } catch (_) {}
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Store access (single cart writer)

  // The cart action + truth surface is <cart-engine> (issue #20). It exposes the
  // same reads the drawer needs (items) and the write actions (changeItem,
  // changeSellingPlan). Resolved at call time so it stays correct across mounts.
  #cartStore() {
    try {
      return window.cartEngine || null;
    } catch (_) { return null; }
  }

  /**
   * Resolve the store line key for a row. `data-line-key` is SSR-rendered on
   * every row; the (variantId, planId) match against store state is a safety
   * net for rows whose key attribute is missing/stale.
   */
  #resolveLineKey(row) {
    try {
      const direct = row && row.getAttribute('data-line-key');
      if (direct && direct.trim() !== '') return direct;
      const cart = this.#cartStore();
      if (!cart || !Array.isArray(cart.items)) return null;
      const vid = Number(row && row.getAttribute('data-variant-id'));
      if (!vid) return null;
      let planId = '';
      try {
        planId = (row.getAttribute('data-plan-id') || '').trim();
        if (!planId) {
          const sel = row.querySelector && row.querySelector('select[data-selling-plan]');
          if (sel) planId = (sel.value || sel.getAttribute('data-selected-plan') || '').trim();
        }
      } catch (_) { planId = ''; }
      const match = cart.items.find((it) => {
        if (!it || Number(it.variant_id) !== vid) return false;
        const itPlan = it.selling_plan_allocation?.selling_plan?.id;
        return String(itPlan || '') === String(planId || '');
      });
      return (match && match.key) || null;
    } catch (_) { return null; }
  }

  #lineQuantity(lineKey, row) {
    try {
      const cart = this.#cartStore();
      const item = cart && Array.isArray(cart.items) && cart.items.find((i) => i.key === lineKey);
      if (item) return Number(item.quantity) || 0;
    } catch (_) {}
    try {
      const qtyInput = row && row.querySelector('input[name="quantity"]');
      const qtySpan = row && row.querySelector('[data-role="qty"]');
      return qtyInput
        ? parseInt(qtyInput.value, 10) || 0
        : (qtySpan ? parseInt(qtySpan.textContent, 10) || 0 : parseInt(row?.getAttribute('data-quantity'), 10) || 0);
    } catch (_) { return 0; }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Click delegation: +/- buttons and remove → store actions

  #onClick = (ev) => {
    try {
      const btn = ev.target && ev.target.closest && ev.target.closest('[data-action]');
      if (!btn) return;
      const action = btn.getAttribute('data-action');
      if (action !== 'inc' && action !== 'dec' && action !== 'remove') return;
      ev.preventDefault();
      const row = this.#getRowFromTarget(btn);
      const lineKey = this.#resolveLineKey(row);
      const cart = this.#cartStore();
      if (!lineKey || !cart) return;

      const currentQty = this.#lineQuantity(lineKey, row);
      const change = (quantity) => cart.changeItem({ key: lineKey, quantity }).catch(() => {});

      if (action === 'inc') return this.#withButtonLoading(btn, () => change(currentQty + 1));
      if (action === 'dec') return this.#withButtonLoading(btn, () => change(Math.max(0, currentQty - 1)));
      if (action === 'remove') return change(0);
    } catch (_) {}
  };

  async #withButtonLoading(button, task) {
    try {
      if (!button || typeof task !== 'function') return await task();
      const row = button.closest('[data-variant-id]');
      const decBtn = row && row.querySelector('[data-action="dec"]');
      const incBtn = row && row.querySelector('[data-action="inc"]');
      const allBtns = [decBtn, incBtn].filter(Boolean);
      const qtyInput = row && row.querySelector('input[name="quantity"]');
      const qtySpan = row && row.querySelector('[data-role="qty"]');

      allBtns.forEach((b) => { try { b.disabled = true; } catch (_) {} });
      try {
        const i = button.querySelector('[data-role="icon"]');
        if (i) i.style.display = 'none';
      } catch (_) {}
      try { if (qtyInput) qtyInput.disabled = true; else if (qtySpan) qtySpan.style.opacity = '0.5'; } catch (_) {}

      const spin = button.querySelector('[data-role="spinner"]');
      if (spin) { spin.classList.remove('hidden'); try { spin.style.display = 'inline-flex'; } catch (_) {} }

      try {
        return await task();
      } finally {
        if (spin) { spin.classList.add('hidden'); try { spin.style.display = ''; } catch (_) {} }
        allBtns.forEach((b) => {
          try {
            const i = b.querySelector('[data-role="icon"]');
            if (i) { try { i.style.display = ''; } catch (_) {} }
            b.disabled = false;
          } catch (_) {}
        });
        try { if (qtyInput) qtyInput.disabled = false; else if (qtySpan) qtySpan.style.opacity = ''; } catch (_) {}
      }
    } catch (e) { throw e; }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // After a cart update: the section (count, lines, subtotal, upsells) is
  // already morphed by the engine's data-cart-section response. The only thing
  // left is re-hydrating selling-plan selects (currently disabled in
  // cart-item.liquid; no-op when no select exists).

  #applyCart() {
    try {
      const selects = this.querySelectorAll('select[data-selling-plan]');
      selects.forEach((s) => { try { this.#ensurePlanOptions(s); } catch (_) {} });
    } catch (_) {}
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Form change delegation: selling-plan + qty input → store actions

  #onChange = (ev) => {
    try {
      const target = ev.target;
      const select = target && target.closest && target.closest('select[data-selling-plan]');
      if (select) {
        ev.preventDefault();
        const row = this.#getRowFromTarget(select);
        const lineKey = this.#resolveLineKey(row);
        const cart = this.#cartStore();
        if (!lineKey || !cart) return;
        const planId = select.value && select.value !== '' ? Number(select.value) : null;
        // Atomic store action: remove + re-add, rolled back on failure.
        cart.changeSellingPlan({ key: lineKey, sellingPlanId: planId });
        return;
      }
      const qty = target && target.matches && target.matches('input[name="quantity"]') ? target : null;
      if (qty) {
        ev.preventDefault();
        const row = this.#getRowFromTarget(qty);
        const lineKey = this.#resolveLineKey(row);
        const cart = this.#cartStore();
        if (!lineKey || !cart) return;
        let next = parseInt(qty.value, 10) || 0;
        const min = parseInt(qty.getAttribute('min') || '0', 10);
        const max = parseInt(qty.getAttribute('max') || '9999', 10);
        if (Number.isFinite(min)) next = Math.max(min, next);
        if (Number.isFinite(max)) next = Math.min(max, next);

        const decBtn = row.querySelector('[data-action="dec"]');
        const incBtn = row.querySelector('[data-action="inc"]');
        const run = () => cart.changeItem({ key: lineKey, quantity: next }).catch(() => {});
        if (incBtn) this.#withButtonLoading(incBtn, run);
        else if (decBtn) this.#withButtonLoading(decBtn, run);
        else run();
      }
    } catch (_) {}
  };

  #getRowFromTarget(el) {
    try {
      const byArticle = el && el.closest && el.closest('article[data-line-key]');
      if (byArticle) return byArticle;
      const path = (typeof el.composedPath === 'function') ? el.composedPath() : [];
      for (let i = 0; i < path.length; i += 1) {
        const n = path[i];
        try {
          if (n && n.nodeType === 1 && n.matches && n.matches('article[data-line-key]')) return n;
        } catch (_) {}
      }
      const byKey = el && el.closest && el.closest('[data-line-key]');
      if (byKey) return byKey;
      return el && el.closest && (el.closest('article[data-variant-id]') || el.closest('[data-variant-id]'));
    } catch (_) {
      return (el && el.closest && (el.closest('[data-line-key]') || el.closest('[data-variant-id]'))) || null;
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Selling plan dropdown: lazy-hydrate options on first focus
  // (products/<handle>.js is product data, not a cart route)

  #onPlanFocus = async (ev) => {
    try {
      const select = ev.target && ev.target.closest && ev.target.closest('select[data-selling-plan]');
      if (!select) return;
      this.#ensurePlanOptions(select);
    } catch (_) {}
  };

  #ensurePlanOptions = async (select) => {
    try {
      if (!select || select.getAttribute('data-hydrated') === 'true') return;
      const row = select.closest('[data-variant-id]');
      const handle = row && row.getAttribute('data-handle');
      if (!handle) return;
      let data = this._planCache[handle];
      if (!data) {
        const res = await fetch(`${window.Theme.routes.root}products/${handle}.js`, { headers: { 'Accept': 'application/json' } });
        data = await res.json();
        this._planCache[handle] = data;
      }
      const groups = (data && data.selling_plan_groups) || [];
      const selected = select.getAttribute('data-selected-plan') || '';
      const opts = [];
      groups.forEach((g) => {
        (g.selling_plans || []).forEach((p) => {
          const id = Number(p.id);
          const name = p.name || `Plan ${id}`;
          const sel = String(id) === String(selected) ? ' selected' : '';
          opts.push(`<option value="${id}"${sel}>${name}</option>`);
        });
      });
      if (!opts.length) { select.setAttribute('data-hydrated', 'true'); return; }
      let oneTimeText = 'One-time purchase';
      try {
        const ssrFirst = select.querySelector('option[value=""]') || (select.options && select.options[0]);
        const txt = ssrFirst && (ssrFirst.textContent || ssrFirst.innerText);
        if (txt && String(txt).trim()) oneTimeText = String(txt).trim();
      } catch (_) {}
      try {
        const dialogOneTime = this.closest('store-dialog')?.getAttribute('data-i18n-cart-one-time');
        if (dialogOneTime) oneTimeText = dialogOneTime;
      } catch (_) {}
      try { oneTimeText = document.documentElement.getAttribute('data-i18n-cart-one-time') || oneTimeText; } catch (_) {}
      const first = `<option value="">${escapeHtml(oneTimeText)}</option>`;
      select.innerHTML = first + opts.join('');
      if (selected && opts.length) select.value = String(selected);
      select.setAttribute('data-hydrated', 'true');
    } catch (_) {}
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Subtotal money formatter (uses Shopify's global formatter when present)

  #formatMoney(cents) {
    try {
      const value = typeof cents === 'number' ? Math.round(cents) : parseInt(String(cents || 0), 10) || 0;
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        const fmt = window.Shopify.money_format || undefined;
        return window.Shopify.formatMoney(value, fmt);
      }
      return (value / 100).toFixed(2);
    } catch (_) {
      try { return (Number(cents || 0) / 100).toFixed(2); } catch (__) { return String(cents); }
    }
  }

  #open() {
    try {
      const id = this.dialogId;
      if (!id) return;
      if (window.store && window.store.dialog && window.store.dialog.open) {
        window.store.dialog.open(id);
      } else {
        document.getElementById(id)?.setAttribute('data-open', '');
      }
    } catch (_) {}
  }
}

function escapeHtml(s) {
  try { const d = document.createElement('div'); d.textContent = String(s || ''); return d.innerHTML; }
  catch (_) { return String(s || ''); }
}

try { if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer); } catch (_) {}
